import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import $ from "jquery";
import "datatables.net";
import SubscriptionService from "../../services/SubscriptionService";
import {
  SaCard,
  SaPrimaryButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";

function toLocalDateTimeValue(v) {
  if (v == null || v === "") return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return typeof v === "string" ? v.slice(0, 16) : "";
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localNowForInput() {
  const d = new Date();
  d.setSeconds(0, 0);
  return toLocalDateTimeValue(d);
}

function addMonthsLocal(startStr, months) {
  const d = new Date(startStr);
  if (Number.isNaN(d.getTime())) return "";
  const m = Number(months);
  if (!Number.isFinite(m) || m < 1) return toLocalDateTimeValue(d);
  d.setMonth(d.getMonth() + m);
  return toLocalDateTimeValue(d);
}

function planDurationMonths(plan) {
  const n = plan?.durationMonths ?? plan?.DurationMonths;
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) && v >= 1 ? v : 12;
}

function AddSubscription({ ClientId, onSubscriptionsChanged }) {
  const navigate = useNavigate();
  const toast = useRef(null);
  const tableRef = useRef(null);
  const [form, setForm] = useState({
    subscriptionPlanId: "",
    price: "",
    startDate: "",
    endDate: "",
    clientId: ClientId,
  });
  const [formError, setFormError] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patchingId, setPatchingId] = useState(null);

  const fetchSubscriptions = useCallback(async () => {
    if (!ClientId) return;
    try {
      const rows = await SubscriptionService.getSubscriptionsByClientId(ClientId);
      setSubscriptions(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load subscriptions.",
        life: 4000,
      });
    }
  }, [ClientId]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await SubscriptionService.getAllSubscriptionPlans();
        if (!cancelled) setSubscriptionPlans(Array.isArray(plans) ? plans : []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || subscriptions.length === 0) return undefined;
    const t = setTimeout(() => {
      if ($.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().destroy();
      }
      $(el).DataTable({ pageLength: 10, ordering: true });
    }, 0);
    return () => {
      clearTimeout(t);
      if ($.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().destroy();
      }
    };
  }, [subscriptions]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const onPlanChange = (e) => {
    const value = e.target.value;
    const plan = subscriptionPlans.find((p) => String(p.id) === String(value));
    const months = plan ? planDurationMonths(plan) : 12;
    const start = value ? localNowForInput() : "";
    const end = value && start ? addMonthsLocal(start, months) : "";
    const suggested =
      plan != null && plan.suggestedPrice != null && plan.suggestedPrice !== ""
        ? String(plan.suggestedPrice)
        : undefined;

    setForm((prev) => ({
      ...prev,
      subscriptionPlanId: value,
      startDate: value ? start : prev.startDate,
      endDate: value ? end : prev.endDate,
      price: suggested !== undefined ? suggested : prev.price,
    }));
    setFormError((prev) => ({ ...prev, subscriptionPlanId: "" }));
  };

  const onStartDateChange = (e) => {
    const start = e.target.value;
    setForm((prev) => {
      const plan = subscriptionPlans.find((p) => String(p.id) === String(prev.subscriptionPlanId));
      const months = plan ? planDurationMonths(plan) : 12;
      return {
        ...prev,
        startDate: start,
        endDate: prev.subscriptionPlanId && start ? addMonthsLocal(start, months) : prev.endDate,
      };
    });
    setFormError((prev) => ({ ...prev, startDate: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!String(form.subscriptionPlanId).trim()) errors.subscriptionPlanId = "Choose a plan.";
    if (!String(form.price).trim()) errors.price = "Enter price.";
    if (!String(form.startDate).trim()) errors.startDate = "Start date required.";
    if (!String(form.endDate).trim()) errors.endDate = "End date required.";
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  async function saveSubscription(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const requestData = {
      subscriptionPlanId: parseInt(form.subscriptionPlanId, 10),
      price: Number(form.price),
      startDate: form.startDate,
      endDate: form.endDate,
      isActive: true,
      isPaid: false,
      clientId: parseInt(ClientId, 10),
    };
    try {
      await SubscriptionService.createSubscription(requestData);
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Subscription added.",
        life: 3000,
      });
      setForm({
        subscriptionPlanId: "",
        price: "",
        startDate: "",
        endDate: "",
        clientId: ClientId,
      });
      await fetchSubscriptions();
      if (onSubscriptionsChanged) await onSubscriptionsChanged();
    } catch (error) {
      if (error.response?.status === 400 && error.response.data?.errors) {
        setFormError(error.response.data.errors);
      }
      const detail =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        "Could not save subscription.";
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail,
        life: 5000,
      });
      console.error(error);
    } finally {
      setSaving(false);
    }
  }

  const patchSubscriptionFields = async (sub, patch) => {
    setPatchingId(sub.id);
    try {
      await SubscriptionService.patchSubscription(sub.id, patch);
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: "Subscription saved.",
        life: 2200,
      });
      await fetchSubscriptions();
      if (onSubscriptionsChanged) await onSubscriptionsChanged();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Update failed.",
        life: 4000,
      });
      console.error(err);
    } finally {
      setPatchingId(null);
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    try {
      await SubscriptionService.deleteSubscription(subscriptionId);
      toast.current?.show({
        severity: "success",
        summary: "Removed",
        detail: "Subscription deleted.",
        life: 2500,
      });
      setSubscriptions((prev) => prev.filter((s) => s.id !== subscriptionId));
      if (onSubscriptionsChanged) await onSubscriptionsChanged();
    } catch (e) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete subscription.",
        life: 3500,
      });
      console.error(e);
    }
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "0 18px",
  };

  const selectSx = {
    ...saInputSx,
    minWidth: "120px",
    padding: "8px 10px",
    fontSize: "13px",
    cursor: "pointer",
  };

  return (
    <div>
      <Toast ref={toast} />
      <form onSubmit={saveSubscription}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-receipt">Subscription</SaSectionTitle>
          <p style={{ margin: "-8px 0 18px", fontSize: "13px", color: SA.muted }}>
            Pick a plan to prefill billing period from its duration (months) and suggested price when set. Dates block
            overlap with another <strong>active</strong> subscription that is not yet expired—you can add a new line once the
            current period has ended. Plans are edited under{" "}
            <strong>Subscription plans</strong> (<code>/subscriptionplans</code>).
          </p>
          <div style={grid}>
            <SaField label="Plan *">
              <select
                name="subscriptionPlanId"
                value={form.subscriptionPlanId}
                onChange={onPlanChange}
                style={{ ...saInputSx, borderColor: formError.subscriptionPlanId ? SA.danger : SA.border }}
              >
                <option value="">{loadingPlans ? "Loading plans…" : "Select plan"}</option>
                {!loadingPlans &&
                  subscriptionPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.subscriptionPlanName}
                      {p.durationMonths != null ? ` (${p.durationMonths} mo)` : ""}
                    </option>
                  ))}
              </select>
            </SaField>
            <SaField label="Price *">
              <input
                type="number"
                step="0.01"
                name="price"
                value={form.price}
                onChange={changeHandler}
                style={{ ...saInputSx, borderColor: formError.price ? SA.danger : SA.border }}
              />
            </SaField>
            <SaField label="Start *">
              <input
                type="datetime-local"
                name="startDate"
                value={form.startDate}
                onChange={onStartDateChange}
                style={{ ...saInputSx, borderColor: formError.startDate ? SA.danger : SA.border }}
              />
            </SaField>
            <SaField label="End *">
              <input
                type="datetime-local"
                name="endDate"
                value={form.endDate}
                onChange={changeHandler}
                style={{ ...saInputSx, borderColor: formError.endDate ? SA.danger : SA.border }}
              />
            </SaField>
          </div>
          <SaFormActions>
            <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-plus"}>
              {saving ? "Saving…" : "Add subscription"}
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      </form>

      <div style={{ marginTop: "22px" }}>
        <SaCard noPadding>
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${SA.border}` }}>
            <span style={{ fontSize: "14px", fontWeight: "800", color: SA.ink }}>History</span>
          </div>
          {subscriptions.length === 0 ? (
            <div style={{ padding: "36px 24px", textAlign: "center", color: SA.muted, lineHeight: 1.6 }}>
              <i className="fas fa-receipt" style={{ fontSize: "28px", opacity: 0.35, marginBottom: "12px", display: "block" }} />
              <div style={{ fontWeight: "700", color: SA.ink, marginBottom: "6px" }}>
                No subscriptions recorded for this client
              </div>
              Add a plan above to activate tenant access. Without an active subscription, Client ID {ClientId} users
              are blocked from modules and API calls.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table ref={tableRef} className="table mb-0" style={{ width: "100%" }}>
                <thead style={{ background: SA.bgSoft }}>
                  <tr>
                    {["Plan", "Price", "Start", "End", "Expired", "Active", "Payment", "Actions"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          fontSize: "11px",
                          fontWeight: "800",
                          color: SA.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => {
                    const end = sub.endDate ? new Date(sub.endDate) : null;
                    const expired = end && !Number.isNaN(end.getTime()) ? end.getTime() < Date.now() : false;
                    const patchBusy = patchingId === sub.id;
                    return (
                      <tr key={sub.id}>
                        <td style={{ padding: "12px 16px", fontSize: "13px", fontWeight: "600", color: SA.ink }}>
                          {sub.subscriptionPlan?.subscriptionPlanName ?? "—"}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{sub.price}</td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>
                          {sub.startDate ? new Date(sub.startDate).toLocaleString() : "—"}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>
                          {sub.endDate ? new Date(sub.endDate).toLocaleString() : "—"}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: "700",
                              background: expired ? "#fff3e0" : "#e3f2fd",
                              color: expired ? "#e65100" : "#1565c0",
                            }}
                          >
                            {expired ? "Yes" : "No"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <select
                            aria-label="Active status"
                            value={sub.isActive ? "yes" : "no"}
                            disabled={patchBusy}
                            onChange={(e) => {
                              const next = e.target.value === "yes";
                              if (next === !!sub.isActive) return;
                              patchSubscriptionFields(sub, { isActive: next });
                            }}
                            style={{ ...selectSx, borderColor: SA.border }}
                          >
                            <option value="yes">Active</option>
                            <option value="no">Inactive</option>
                          </select>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <select
                            aria-label="Payment status"
                            value={sub.isPaid ? "paid" : "unpaid"}
                            disabled={patchBusy}
                            onChange={(e) => {
                              const next = e.target.value === "paid";
                              if (next === !!sub.isPaid) return;
                              patchSubscriptionFields(sub, { isPaid: next });
                            }}
                            style={{ ...selectSx, borderColor: SA.border }}
                          >
                            <option value="unpaid">Unpaid</option>
                            <option value="paid">Paid</option>
                          </select>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => navigate(`/editsubscription/${sub.id}`)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              marginRight: "8px",
                              cursor: "pointer",
                              color: SA.primary,
                            }}
                          >
                            <i className="fas fa-pen" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => deleteSubscription(sub.id)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "34px",
                              height: "34px",
                              cursor: "pointer",
                              color: SA.danger,
                            }}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SaCard>
      </div>
    </div>
  );
}

export default AddSubscription;
